import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "./ui/button";
import { twMerge } from "tailwind-merge";
import ReferralIcon from "./svgs/ReferralIcon";
import SupportIcon from "./svgs/SupportIcon";
import ShopIcon from "./svgs/ShopIcon";
import { ShopLink, SupportLink } from "../utils/links";

const Navbar = ({ loggedIn }) => {
  const path = useLocation().pathname;

  const [mactive, setMactive] = useState(false);

  const [isWebView, setIsWebView] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    setMactive(false);
  }, [path]);

  useEffect(() => {
    if (
      window.client === "colla_app" ||
      localStorage.getItem("iscollawebview") === "true"
    )
      setIsWebView(true);
  }, []);

  return isWebView ? null : (
    <div className="w-full lg:p-3 sticky top-0 left-0 z-20">
      <div className="bg-white w-full relative z-10 h-20 lg:rounded-xl max-w-7xl mx-auto border flex flex-row items-center justify-between px-4">
        <Link to="/">
          <img
            src="/assets/branding/logo.svg"
            alt="logo"
            className="h-10 lg:ml-4 cursor-pointer"
          />
        </Link>

        {loggedIn && (
          <div className="hidden lg:flex flex-row items-center gap-6">
            <NavLink to="/manage">{t("navigation.main.manage")}</NavLink>
            <NavLink to="/activate">{t("navigation.main.activate")}</NavLink>
            <NavLink to="/account">{t("navigation.main.account")}</NavLink>
          </div>
        )}

        <div
          className={twMerge(
            "hidden lg:flex flex-row items-center gap-6",
            isWebView && loggedIn && "hidden",
          )}
        >
          {!isWebView && (
            <>
              {loggedIn && (
                <NavLink to="/referrals" aria-label="Referrals">
                  <ReferralIcon />
                  <span className="hidden xl:block">
                    {t("navigation.main.referral")}
                  </span>
                </NavLink>
              )}

              <NavLink to={SupportLink} target="_blank" aria-label="Support">
                <SupportIcon />
                <span className="hidden xl:block">
                  {t("navigation.main.support")}
                </span>
              </NavLink>

              <NavLink to={ShopLink} target="_blank" aria-label="Shop">
                <ShopIcon />
                <span className="hidden xl:block">
                  {t("navigation.main.shop")}
                </span>
              </NavLink>
            </>
          )}

          {loggedIn ? null : (
            <>
              <Link to="/login" className="hidden lg:block" aria-label="Login">
                <Button variant="outline">{t("navigation.main.login")}</Button>
              </Link>

              <Link
                to="/signup"
                className="hidden lg:block"
                aria-label="Signup"
              >
                <Button>Sign up</Button>
              </Link>
            </>
          )}
        </div>

        <button onClick={() => setMactive(!mactive)} className="lg:hidden">
          {mactive ? (
            <img
              src="/assets/icons/close.svg"
              alt="menu-icon"
              className="h-6"
            />
          ) : (
            <img src="/assets/icons/menu.svg" alt="menu-icon" className="h-6" />
          )}
        </button>
      </div>
      <MobileMenu show={mactive} loggedIn={loggedIn} isWebView={isWebView} />
    </div>
  );
};

const MobileMenu = ({ show, loggedIn, isWebView }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`absolute left-0 w-full overflow-hidden lg:hidden bg-white h-auto shadow-lg`}
      style={{
        maxHeight: show ? 360 : 0,
        transition: "max-height 0.25s ease",
      }}
    >
      <div className="flex flex-col items-stretch gap-6 p-4">
        {loggedIn && (
          <>
            <NavLink to="/manage">{t("navigation.main.manage")}</NavLink>
            <NavLink to="/activate">{t("navigation.main.activate")}</NavLink>
            <NavLink to="/account">{t("navigation.main.account")}</NavLink>
            {!isWebView && (
              <NavLink to="/referrals" aria-label="Referrals">
                <ReferralIcon />
                <span>{t("navigation.main.referral")}</span>
              </NavLink>
            )}
          </>
        )}
        {!isWebView && (
          <>
            <NavLink to={SupportLink} target="_blank" aria-label="Support">
              <SupportIcon />
              <span>{t("navigation.main.support")}</span>
            </NavLink>

            <NavLink to={ShopLink} target="_blank" aria-label="Shop">
              <ShopIcon />
              <span>{t("navigation.main.shop")}</span>
            </NavLink>
          </>
        )}

        {loggedIn ? null : (
          <div className="flex sm:grid sm:grid-cols-2 flex-col gap-3 items-stretch">
            <Link to="/login">
              <Button variant="outline" className="w-full">
                {t("navigation.main.login")}
              </Button>
            </Link>

            <Link to="/signup">
              <Button className="w-full">Sign up</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const NavLink = (props) => {
  const path = "/" + useLocation().pathname.split("/")[1];
  const { children, className, to, ...rest } = props;
  return (
    <Link
      {...rest}
      to={to}
      className={twMerge(
        `${
          path === to ? "text-secondary" : "text-text-button"
        } hover:text-secondary-dark transition-color duration-300 font-medium flex flex-row items-center gap-2`,
        className,
      )}
    >
      {children}
    </Link>
  );
};

export default Navbar;
